"use client"

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { DataTable } from '@open-mercato/ui/backend/DataTable'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import { apiCall, apiCallOrThrow, readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { INVOICE_STATUS_VALUES } from '../../../lib/invoice'

type InvoiceRow = {
  id: string
  invoiceNumber: string
  status: string
  transportMode: string
  dispatchId: string | null
  issuerName: string
  recipientName: string
  issueDate: string | null
  dueDate: string | null
  currencyCode: string
  netAmount: number
  vatAmount: number
  grossAmount: number
}

type InvoiceListResponse = {
  items?: Array<Record<string, unknown>>
  total?: number
}

const statusBadgeClass: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-800',
  ISSUED: 'bg-blue-100 text-blue-800',
  SENT: 'bg-indigo-100 text-indigo-800',
  PAID: 'bg-green-100 text-green-800',
  OVERDUE: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-gray-100 text-gray-600',
  CREDITED: 'bg-yellow-100 text-yellow-800',
}

function toNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

function mapRow(raw: Record<string, unknown>): InvoiceRow {
  return {
    id: String(raw.id ?? ''),
    invoiceNumber: typeof raw.invoiceNumber === 'string' ? raw.invoiceNumber : '',
    status: typeof raw.status === 'string' ? raw.status : 'DRAFT',
    transportMode: typeof raw.transportMode === 'string' ? raw.transportMode : 'ROAD',
    dispatchId: typeof raw.dispatchId === 'string' ? raw.dispatchId : null,
    issuerName: typeof raw.issuerName === 'string' ? raw.issuerName : '',
    recipientName: typeof raw.recipientName === 'string' ? raw.recipientName : '',
    issueDate: typeof raw.issueDate === 'string' ? raw.issueDate : null,
    dueDate: typeof raw.dueDate === 'string' ? raw.dueDate : null,
    currencyCode: typeof raw.currencyCode === 'string' ? raw.currencyCode : 'PLN',
    netAmount: toNumber(raw.netAmount),
    vatAmount: toNumber(raw.vatAmount),
    grossAmount: toNumber(raw.grossAmount),
  }
}

function formatDate(value: string | null): string {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '-'
  return parsed.toLocaleDateString()
}

function formatMoney(amount: number, currency: string): string {
  return `${amount.toFixed(2)} ${currency}`
}

export default function FreightInvoicesListPage() {
  const t = useT()
  const router = useRouter()
  const orgVersion = useOrganizationScopeVersion()
  const { confirm } = useConfirmDialog()

  const [data, setData] = React.useState<InvoiceRow[]>([])
  const [total, setTotal] = React.useState(0)
  const [page, setPage] = React.useState(1)
  const [pageSize] = React.useState(25)
  const [loading, setLoading] = React.useState(true)
  const [filters, setFilters] = React.useState<Record<string, string>>({})

  const fetchData = React.useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      })
      if (filters.status) params.set('status', filters.status)
      if (filters.dispatchId) params.set('dispatchId', filters.dispatchId)

      const response = await readApiResultOrThrow<InvoiceListResponse>(
        `/api/freight/billing/invoices?${params.toString()}`,
        undefined,
        { errorMessage: t('billing.errors.loadFailed', 'Failed to load invoices.') },
      )
      const items = Array.isArray(response?.items) ? response.items.map(mapRow) : []
      setData(items)
      setTotal(typeof response?.total === 'number' ? response.total : items.length)
    } catch (error) {
      flash(
        error instanceof Error && error.message
          ? error.message
          : t('billing.errors.loadFailed', 'Failed to load invoices.'),
        'error',
      )
      setData([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, filters, t])

  React.useEffect(() => {
    void fetchData()
  }, [fetchData, orgVersion])

  const handleFinalize = React.useCallback(async (row: InvoiceRow) => {
    const confirmed = await confirm({
      title: t('billing.actions.finalizeConfirm', 'Finalize invoice?'),
      text: t('billing.actions.finalizeDescription', 'The invoice number will be assigned and the record will become immutable.'),
    })
    if (!confirmed) return
    try {
      await apiCallOrThrow(`/api/freight/billing/invoices/${encodeURIComponent(row.id)}/finalize`, {
        method: 'POST',
      })
      flash(t('billing.actions.finalized', 'Invoice finalized.'), 'success')
      await fetchData()
    } catch (error) {
      flash(
        error instanceof Error && error.message
          ? error.message
          : t('billing.errors.finalizeFailed', 'Failed to finalize invoice.'),
        'error',
      )
    }
  }, [confirm, fetchData, t])

  const handleDelete = React.useCallback(async (row: InvoiceRow) => {
    const confirmed = await confirm({
      title: t('billing.actions.deleteConfirm', 'Delete invoice?'),
      text: t('billing.actions.deleteDescription', 'Drafts can be deleted. Issued invoices cannot be removed.'),
    })
    if (!confirmed) return
    try {
      await apiCallOrThrow(`/api/freight/billing/invoices/${encodeURIComponent(row.id)}`, {
        method: 'DELETE',
      })
      flash(t('billing.actions.deleted', 'Invoice deleted.'), 'success')
      await fetchData()
    } catch (error) {
      flash(
        error instanceof Error && error.message
          ? error.message
          : t('billing.errors.deleteFailed', 'Failed to delete invoice.'),
        'error',
      )
    }
  }, [confirm, fetchData, t])

  const columns = React.useMemo(() => [
    {
      accessorKey: 'invoiceNumber',
      header: t('billing.fields.invoiceNumber', 'Invoice number'),
      cell: ({ row }: { row: { original: InvoiceRow } }) => (
        <span className="font-medium">{row.original.invoiceNumber || t('billing.fields.draft', '(draft)')}</span>
      ),
    },
    {
      accessorKey: 'status',
      header: t('billing.fields.status', 'Status'),
      cell: ({ row }: { row: { original: InvoiceRow } }) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass[row.original.status] ?? 'bg-gray-100 text-gray-800'}`}>
          {t(`billing.status.${row.original.status}`, row.original.status)}
        </span>
      ),
    },
    {
      accessorKey: 'transportMode',
      header: t('billing.fields.transportMode', 'Mode'),
      cell: ({ row }: { row: { original: InvoiceRow } }) =>
        t(`billing.transport_mode.${row.original.transportMode}`, row.original.transportMode),
    },
    {
      accessorKey: 'issuerName',
      header: t('billing.fields.issuerName', 'Issuer'),
    },
    {
      accessorKey: 'recipientName',
      header: t('billing.fields.recipientName', 'Recipient'),
    },
    {
      accessorKey: 'issueDate',
      header: t('billing.fields.issueDate', 'Issued'),
      cell: ({ row }: { row: { original: InvoiceRow } }) => formatDate(row.original.issueDate),
    },
    {
      accessorKey: 'dueDate',
      header: t('billing.fields.dueDate', 'Due date'),
      cell: ({ row }: { row: { original: InvoiceRow } }) => formatDate(row.original.dueDate),
    },
    {
      accessorKey: 'grossAmount',
      header: t('billing.fields.grossAmount', 'Total gross'),
      cell: ({ row }: { row: { original: InvoiceRow } }) =>
        formatMoney(row.original.grossAmount, row.original.currencyCode),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }: { row: { original: InvoiceRow } }) => {
        const items = [
          {
            id: 'view',
            label: t('common.view', 'View'),
            onSelect: () => router.push(`/backend/billing/invoices/${row.original.id}`),
          },
        ]
        if (row.original.status === 'DRAFT') {
          items.push({
            id: 'finalize',
            label: t('billing.actions.finalize', 'Finalize'),
            onSelect: () => handleFinalize(row.original),
          })
          items.push({
            id: 'delete',
            label: t('common.delete', 'Delete'),
            destructive: true,
            onSelect: () => handleDelete(row.original),
          } as { id: string; label: string; destructive: boolean; onSelect: () => void })
        }
        return <RowActions items={items as never} />
      },
    },
  ], [t, router, handleFinalize, handleDelete])

  const filterDefs = React.useMemo(() => [
    {
      id: 'status',
      label: t('billing.fields.status', 'Status'),
      type: 'select' as const,
      options: INVOICE_STATUS_VALUES.map((value) => ({
        value,
        label: t(`billing.status.${value}`, value),
      })),
    },
  ], [t])

  return (
    <Page>
      <PageBody>
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold">{t('billing.title', 'Freight invoices')}</h1>
        </div>
        <DataTable
          data={data}
          columns={columns}
          isLoading={loading}
          pagination={{
            page,
            pageSize,
            total,
            totalPages: Math.max(1, Math.ceil(total / pageSize)),
            onPageChange: setPage,
          }}
          filters={filterDefs}
          filterValues={filters}
          onFiltersApply={setFilters}
        />
      </PageBody>
    </Page>
  )
}
